import { useRef, useState, useEffect } from "react";
import { Paperclip, X, Eye } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

/**
 * FileInput — reemplazo del <input type="file"> nativo cuyo texto ("No file chosen")
 * aparece en inglés según el idioma del navegador. Este componente oculta el input
 * y muestra un botón custom en español + el nombre del archivo elegido.
 *
 * Props:
 *   - value?: File | null              (opcional; en modo controlado)
 *   - onChange: (file|null) => void
 *   - accept?: string
 *   - disabled?: boolean
 *   - label?: string                   texto del botón cuando no hay archivo
 *   - testid?: string
 *   - className?: string               clases extra para el contenedor
 *   - previewable?: boolean            muestra botón "Ver" para previsualizar el archivo seleccionado (default: true)
 */
export default function FileInput({
  value,
  onChange,
  accept,
  disabled = false,
  label = "Elegir archivo",
  testid,
  className = "",
  previewable = true,
}) {
  const ref = useRef(null);
  const [internal, setInternal] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // modo controlado si `value` viene definido
  const file = value !== undefined ? value : internal;

  useEffect(() => {
    // Sincroniza el input DOM cuando el padre resetea el valor externo a null
    if (value === null && ref.current) ref.current.value = "";
  }, [value]);

  // Crea/libera objectURL cuando cambia el archivo
  useEffect(() => {
    if (!file) {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (_) {}
      }
      setPreviewUrl(null);
      setPreviewOpen(false);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      try { URL.revokeObjectURL(url); } catch (_) {}
    };
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFile = (f) => {
    if (value === undefined) setInternal(f);
    onChange?.(f);
  };

  const isImage = file && (file.type || "").startsWith("image/");
  const isPdf = file && ((file.type || "").includes("pdf") || /\.pdf$/i.test(file.name || ""));
  const canPreview = previewable && (isImage || isPdf);

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => ref.current?.click()}
        className="h-10 px-3 rounded-md bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={testid ? `${testid}-btn` : undefined}
      >
        <Paperclip className="w-4 h-4" />
        {file ? "Cambiar archivo" : label}
      </button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="hidden"
        data-testid={testid}
      />
      {file ? (
        <span className="text-xs text-foreground/80 inline-flex items-center gap-1.5 max-w-[280px]">
          {canPreview && previewUrl && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 hover:bg-emerald-100 text-[11px] font-semibold"
              title="Ver vista previa del archivo seleccionado"
              data-testid={testid ? `${testid}-preview` : undefined}
            >
              <Eye className="w-3.5 h-3.5" />
              Ver
            </button>
          )}
          <span className="truncate" title={file.name}>{file.name}</span>
          <span className="text-muted-foreground shrink-0">· {Math.round(file.size / 1024)} KB</span>
          <button
            type="button"
            onClick={() => { setFile(null); if (ref.current) ref.current.value = ""; }}
            className="text-rose-600 hover:text-rose-700"
            title="Quitar archivo"
            data-testid={testid ? `${testid}-remove` : undefined}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">Sin archivo seleccionado</span>
      )}

      {canPreview && previewUrl && (
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent
            className="max-w-4xl w-[95vw] h-[90vh] p-0 flex flex-col overflow-hidden grid-cols-[minmax(0,1fr)]"
            data-testid={testid ? `${testid}-preview-modal` : undefined}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/40 min-w-0">
              <div className="text-xs font-semibold text-foreground truncate">
                Vista previa · <span className="text-muted-foreground font-normal">{file?.name}</span>
              </div>
              <div className="text-[11px] text-muted-foreground shrink-0">
                {Math.round((file?.size || 0) / 1024)} KB
              </div>
            </div>
            <div className="flex-1 min-h-0 min-w-0 bg-slate-900/95 overflow-auto flex items-center justify-center">
              {isPdf ? (
                <iframe
                  src={previewUrl}
                  title="Vista previa"
                  className="w-full h-full border-0"
                  data-testid={testid ? `${testid}-preview-iframe` : undefined}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt="Vista previa"
                  className="max-w-full max-h-full object-contain select-none"
                  data-testid={testid ? `${testid}-preview-img` : undefined}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
